import React, { useEffect, useRef, useState } from 'react';

const CARDS = [
  {
    num: '01',
    title: 'High-Intent Lead Generation',
    text: 'We source leads who are actively searching for your solution — not cold audiences who need months of nurturing.',
  },
  {
    num: '02',
    title: 'Conversion-Optimized Funnels',
    text: 'Every touchpoint is engineered to push prospects toward a decision. VSLs, email sequences, booking systems that convert.',
  },
  {
    num: '03',
    title: 'AI-Powered Revenue Operations',
    text: 'From chatbots to CRM workflows, our automation stack keeps your pipeline moving 24/7 without adding headcount.',
  },
];

const CHECKLIST = [
  'Specialised in high-intent B2B & B2C lead generation across India and globally',
  'AI-powered CRM automation that ensures zero leads fall through the cracks',
  'Conversion-optimized funnels built for high-ticket sales and scalable pipelines',
  'Dedicated team of paid ads specialists, funnel architects, and AI engineers',
];

const About = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.07 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const anim = (delay) =>
    isVisible
      ? { animation: `aboutFadeUp 0.8s ease-out ${delay + 0.4}s both forwards` }
      : { opacity: 0 };

  const animRight = (delay) =>
    isVisible
      ? { animation: `aboutFadeRight 0.8s ease-out ${delay + 0.4}s both forwards` }
      : { opacity: 0 };

  return (
    <section ref={sectionRef} id="about" className="py-16 md:py-24 lg:py-32 px-4 md:px-6 bg-[#0D1117] relative overflow-hidden">
      <style>{`
        @keyframes aboutFadeUp {
          from { opacity: 0; transform: translateY(30px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes aboutFadeRight {
          from { opacity: 0; transform: translateX(30px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">

          {/* ── Left: Text ── */}
          <div>
            <p
              className="text-xs font-semibold tracking-[0.18em] uppercase text-[#007bff] mb-4"
              style={anim(0)}
            >
              About SGS Global Media
            </p>

            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-5 leading-tight tracking-tight"
              style={anim(0.1)}
            >
              We Don't Run Campaigns.<br />We Install Growth Systems.
            </h2>

            <p
              className="text-gray-400 text-base md:text-lg leading-relaxed mb-8"
              style={anim(0.2)}
            >
              Founded by performance marketing experts, SGS Global Media combines cutting-edge
              AI automation with battle-tested sales funnels to help businesses generate
              predictable, scalable revenue.
            </p>

            <ul className="space-y-4 mb-10" style={anim(0.3)}>
              {CHECKLIST.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-300 text-sm md:text-base">
                  <span className="text-[#007bff] font-bold mt-0.5 flex-shrink-0">✓</span>
                  {item}
                </li>
              ))}
            </ul>

            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-block bg-[#007bff] text-white font-bold px-8 py-4 rounded-2xl glow-button"
              style={anim(0.4)}
            >
              Start Your Growth System →
            </a>
          </div>

          {/* ── Right: Cards ── */}
          <div className="space-y-4">
            {CARDS.map((card, i) => (
              <div
                key={i}
                className="relative bg-[#111620] border border-white/[0.07] rounded-lg p-8 overflow-hidden"
                style={animRight(0.25 + i * 0.12)}
              >
                {/* Gold top accent line */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#007bff] to-transparent" />
                <div className="text-5xl font-extrabold text-[#007bff] opacity-20 leading-none mb-1">
                  {card.num}
                </div>
                <div className="font-bold text-white text-lg mb-2">{card.title}</div>
                <p className="text-gray-400 text-sm leading-relaxed">{card.text}</p>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
};

export default About;